import { HttpErrorResponse } from '@angular/common/http';
import { Observable, map } from 'rxjs';

import {
  ReportDataType,
  ReportOutputKind,
  ReportQuery,
  ReportQueryService,
  ReportWidgetData,
} from '../services/report-query';
import { DashboardWidgetLayout } from './templates/dashboard-grid/dashboard-grid';

export type DashboardOwnerScope = 'user' | 'organisation';

export type DashboardWidgetTemplate =
  | 'record-metric'
  | 'welcome'
  | 'number'
  | 'percentage'
  | 'graph'
  | 'sankey'
  | 'report'
  | 'list'
  | 'position'
  | 'finance-money';

export interface DashboardWidgetSizeLimits {
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
  defaultWidth: number;
  defaultHeight: number;
}

export interface DashboardWidgetDefinition {
  widgetId: string;
  name: string;
  description: string;
  apps: readonly string[];
  template: DashboardWidgetTemplate;
  inputs: Record<string, unknown>;
  dummyData: Record<string, unknown>;
  limits: DashboardWidgetSizeLimits;
}

export interface DashboardWidgetPlacement {
  id: string;
  widgetId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  inputs?: Record<string, unknown>;
}

export interface DashboardLayoutDocument {
  dashboardId: string;
  ownerScope: DashboardOwnerScope;
  source: DashboardOwnerScope | 'default';
  widgets: DashboardWidgetPlacement[];
}

export interface DashboardLayoutSaveRequest {
  ownerScope: DashboardOwnerScope;
  widgets: DashboardWidgetPlacement[];
}

export function clampWidgetPlacement(
  placement: DashboardWidgetPlacement,
  limits: DashboardWidgetSizeLimits,
  columns = 12,
): DashboardWidgetPlacement {
  const width = Math.min(
    Math.max(1, Math.min(columns, limits.maxWidth)),
    Math.max(limits.minWidth, Math.round(placement.width)),
  );
  const height = Math.min(
    Math.max(1, limits.maxHeight),
    Math.max(limits.minHeight, Math.round(placement.height)),
  );
  return {
    ...placement,
    x: Math.max(0, Math.min(columns - width, Math.round(placement.x))),
    y: Math.max(0, Math.round(placement.y)),
    width,
    height,
  };
}

export function normalizeDashboardPlacements(
  placements: DashboardWidgetPlacement[],
  definitions: readonly DashboardWidgetDefinition[],
  columns = 12,
): DashboardWidgetPlacement[] {
  const limits = new Map(definitions.map((definition) => [definition.widgetId, definition.limits]));
  const occupied = new Set<string>();

  return placements
    .map((placement) => {
      const definitionLimits = limits.get(placement.widgetId) ?? {
        minWidth: 1,
        maxWidth: columns,
        minHeight: 1,
        maxHeight: 12,
        defaultWidth: 3,
        defaultHeight: 2,
      };
      const item = clampWidgetPlacement(placement, definitionLimits, columns);
      while (overlaps(item, occupied)) item.y += 1;
      occupy(item, occupied);
      return item;
    });
}

function overlaps(placement: DashboardWidgetPlacement, occupied: ReadonlySet<string>): boolean {
  for (let y = placement.y; y < placement.y + placement.height; y += 1) {
    for (let x = placement.x; x < placement.x + placement.width; x += 1) {
      if (occupied.has(`${x}:${y}`)) return true;
    }
  }
  return false;
}

function occupy(placement: DashboardWidgetPlacement, occupied: Set<string>): void {
  for (let y = placement.y; y < placement.y + placement.height; y += 1) {
    for (let x = placement.x; x < placement.x + placement.width; x += 1) {
      occupied.add(`${x}:${y}`);
    }
  }
}

export type DashboardValueFormat = 'number' | 'currency' | 'percentage';
export type DashboardColor = 'primary' | 'success' | 'danger' | 'warning' | 'purple' | 'azure';
export type DashboardIcon = 'money' | 'orders' | 'users' | 'trend' | 'briefcase' | 'percentage';

export interface DashboardTimeWindowOption {
  label: string;
  duration: number;
  from?: string;
}

export interface DashboardScalar {
  value: number;
  dataType: ReportDataType;
  data: ReportWidgetData;
}

export function loadDashboardScalar(
  service: ReportQueryService,
  query: ReportQuery,
  output: Extract<ReportOutputKind, 'number' | 'percentage'> = 'number',
): Observable<DashboardScalar> {
  return service.loadWidget(query, output).pipe(
    map((data) => {
      const key = data.verification.bindings.value;
      const raw = key ? data.result.rows[0]?.[key] : 0;
      const value = typeof raw === 'number' ? raw : Number(raw ?? 0);
      const dataType = data.result.columns.find((column) => column.key === key)?.data_type ?? 'number';
      return { value: Number.isFinite(value) ? value : 0, dataType, data };
    }),
  );
}

export function dashboardWindowQuery(
  service: ReportQueryService,
  query: ReportQuery,
  option: DashboardTimeWindowOption,
): ReportQuery {
  const from = option.from ?? relativeFrom(option.duration);
  return service.withTimeWindow(query, from, option.duration);
}

export function relativeFrom(duration: number): string {
  const from = new Date();
  from.setUTCHours(0, 0, 0, 0);
  from.setUTCDate(from.getUTCDate() - Math.max(1, duration) + 1);
  return from.toISOString();
}

export function formatDashboardValue(
  value: number,
  format: DashboardValueFormat,
  currencyCode = 'USD',
  locale = 'en-US',
): string {
  if (format === 'currency') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (format === 'percentage') {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)}%`;
  }
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
}

export function dashboardErrorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 403) {
      return "You don't have permission to view this widget's data. Ask your organisation administrator for access.";
    }
    const body = error.error as { error?: { message?: string }; message?: string } | string | null;
    if (typeof body === 'string' && body.trim()) return body;
    if (body && typeof body === 'object') {
      return body.error?.message ?? body.message ?? `RQL request failed (${error.status}).`;
    }
    return `RQL request failed (${error.status}).`;
  }
  return error instanceof Error ? error.message : 'The dashboard widget could not load.';
}

export function dashboardLayoutStyle(
  layout: DashboardWidgetLayout,
  accent?: string,
): string {
  const xs = normalize(layout.xs);
  const sm = normalize(layout.sm ?? xs);
  const md = normalize(layout.md ?? sm);
  const lg = normalize(layout.lg ?? md);
  const xl = normalize(layout.xl ?? lg);
  return [
    `--widget-xs-w:${xs.width}`,
    `--widget-xs-h:${xs.height}`,
    `--widget-sm-w:${sm.width}`,
    `--widget-sm-h:${sm.height}`,
    `--widget-md-w:${md.width}`,
    `--widget-md-h:${md.height}`,
    `--widget-lg-w:${lg.width}`,
    `--widget-lg-h:${lg.height}`,
    `--widget-xl-w:${xl.width}`,
    `--widget-xl-h:${xl.height}`,
    accent ? `--widget-accent:${accent}` : '',
  ].filter(Boolean).join(';');
}

function normalize(size: { width: number; height: number }) {
  return {
    width: Math.max(1, Math.min(12, Math.round(size.width))),
    height: Math.max(1, Math.round(size.height)),
  };
}
